import React, { useEffect, useState } from 'react';
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
} from '@react-pdf/renderer';
import QRCode from 'qrcode';
import { format } from 'date-fns';
import { IDeliveryBatch } from '../../../interfaces/delivery-company/tasks';
import { deliveryBatchToQRcodeID } from '../../../utils/delivery-tasks';
import { OrderType } from '../../../interfaces/delivery-company/orders';
import { encodePermanentPackage } from '../../../utils/encoding';
import { displayLocation } from '../../../utils/locations';
import { Workflow } from '../../../enums/orders';
import { display_phone_number } from '../../../utils/phone_numbers';
import { registerFonts } from '../../../utils/pdf-fonts';
import { displayMoney, isNonZero } from '../../../utils/money';
import { get_cod } from '../../../utils/cod';
import { getPackageDescription } from '../../../utils/order';

registerFonts();

const styles = StyleSheet.create({
  page: {
    padding: 2,
    fontSize: 14,
    fontFamily: 'Noto Sans Arabic',
    backgroundColor: '#fff',
    display: 'flex',
    flexDirection: 'row', // Horizontal layout
    alignItems: 'flex-start', // Align content to the top
  },
  qrContainer: {
    width: '40%', // QR code takes up 40% of the width
    alignItems: 'center',
    justifyContent: 'center',
  },
  qrCode: {
    width: 100, // Size of the QR code
    height: 100,
  },
  contentContainer: {
    width: '60%', // Remaining space for other data
    paddingLeft: 5, // Add some spacing between QR code and content
    paddingRight: 5,
    marginTop: 10,
    fontWeight: 'bold',
  },
  header: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 2,
    color: '#222',
  },
  subHeader: {
    fontSize: 12,
    fontWeight: 'bold',
    marginBottom: 5,
    color: '#222',
  },
  section: {
    marginBottom: 2,
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 1,
    fontSize: 13,
  },
  cell: {
    fontSize: 11,
    color: '#333',
  },
  date: {
    fontSize: 8,
  },
  footer: {
    position: 'absolute',
    bottom: 10,
    right: 10,
    fontSize: 11,
    color: '#555',
  },
});

export const generateQRCode = async (text: string) => {
  try {
    return await QRCode.toDataURL(text);
  } catch (err) {
    console.error(err);
    return '';
  }
};

interface WaybillProps {
  orders: OrderType[];
  orgId: string;
}

interface OnePagerProps {
  order: OrderType;
  orgId: string;
  package_index: number;
}

const MAX_DESCRIPTION_LENGTH = 50; // adjust as needed

const trimDescription = (desc: string) => {
  if (!desc) return '';
  return desc.length > MAX_DESCRIPTION_LENGTH
    ? desc.slice(0, MAX_DESCRIPTION_LENGTH) + '...'
    : desc;
};

const OrderSticker = ({ order, package_index, orgId }: OnePagerProps) => {
  const [qrCode, setQRCode] = useState('');

  useEffect(() => {
    generateQRCode(encodePermanentPackage(order, package_index + 1)).then(
      setQRCode
    );
  }, [order.order_id]);

  const shipmentSources = [
    <Text key="merchant" style={styles.cell}>
      <Text>
        {order.merchant?.name} {order.merchant_location?.contact_phone}
        {'\n'}
      </Text>
    </Text>,
    <Text key="customer" style={styles.cell}>
      <Text>
        {order.customer?.name} {order.customer?.phone_number}
        {'\n'}{' '}
        {order.customer?.secondary_phone_number && (
          <>{display_phone_number(order.customer.secondary_phone_number)}</>
        )}{' '}
      </Text>
    </Text>,
  ];

  const customer_area = order.customer_location?.area

  let customer_address_override = ""

  if (order.customer_location?.area && order.customer_location.area.code.startsWith("EXT")) {
    customer_address_override = displayLocation(order.customer_location)
  }

  if (customer_address_override.length > 65) customer_address_override = customer_address_override.slice(0, 65) + "..."

  return (
    <Page
      size={{ width: 150, height: 275 }}
      style={styles.page}
      orientation="landscape"
      wrap={false}
    >
      {/* QR Code at the top-left corner */}
      <View style={styles.qrContainer}>
        {qrCode && <Image style={styles.qrCode} src={qrCode} />}
        <Text style={{fontWeight: 'bold'}}>{encodePermanentPackage(order, package_index + 1)}</Text>
        {order.merchant?.name == "The Prime Ink" && order.reference_id && <View style={styles.header}><Text>Ref #{order.reference_id}</Text></View>}
        {order.description && 
          (order.packages_metadata == undefined ||  !order.packages_metadata[package_index].description) &&
          order.merchant?.name != "The Prime Ink" && (
          <Text
            style={{
              fontSize: 9,
              marginTop: 2,
              maxWidth: 99,
              color: '#444',
              textAlign: 'center',
            }}
          >
            {trimDescription(order.description)}
          </Text>
        )}
        <Text
          style={{
            fontSize: 8,
            marginTop: 2,
            maxWidth: 99,
            color: '#444',
            textAlign: 'center',
          }}
        >
          {trimDescription(getPackageDescription(order, package_index + 1))}
        </Text>
      </View>

      {/* Other data to the right of the QR code */}
      <View style={styles.contentContainer}>
        <View style={styles.header}>
          <Text>
            Order #{order.order_id} ({package_index + 1}/
            {order.number_of_packages})
          </Text>

          {order.is_exchange && (
            <Text style={{ textDecoration: 'underline' }}>+ Exchange</Text>
          )}
        </View>

        <View style={styles.subHeader}>
          <Text>
           {order.merchant?.name != "The Prime Ink" && order.reference_id && <>Ref #{order.reference_id}</>}   COD: {isNonZero(get_cod(order)) && displayMoney(get_cod(order))}
          </Text>
        </View>
       
        {order.labels && order.labels.length > 0 && (
          <View style={styles.row}>
            <Text style={styles.cell}>
              {order.labels
                .map((item) => `${item.key}:${item.value}`)
                .join('  |  ')}
            </Text>
          </View>
        )}

        <View style={styles.row }>
          <Text style={styles.cell}>
            {order.workflow === Workflow.STANDARD
              ? shipmentSources[0]
              : shipmentSources[1]}{' '}
            {'\n'}
          </Text>
        </View>
        <View style={styles.row}>
          <Text style={styles.cell}>
            {order.workflow === Workflow.STANDARD
              ? shipmentSources[1]
              : shipmentSources[0]}
          </Text>
        </View>
        {'\n\n'}
        <Text style={styles.cell}>
          {!order.customer_location?.area || !order.merchant_location?.area ? (
            <>ERROR: order has incomplete addresses.</>
          ) : (
            <>
              Area:{' '}
              {order.workflow === Workflow.STANDARD
                ? (customer_address_override.length > 0 ? customer_address_override : `${order.customer_location?.area?.name_en} - ${order.customer_location?.area?.name_ar}` )
                : `${order.merchant_location?.area?.name_en} - ${order.merchant_location?.area?.name_ar}`}
              
              {

            !(order.workflow === Workflow.STANDARD && customer_address_override.length > 0) && <>
                 {'\n\n'}
              District:{' '}
              {order.workflow === Workflow.STANDARD
                ? order.customer_location.area.district
                : order.merchant_location.area.district}
            </>
              }
             
            </>
          )}
        </Text>
        <View style={{ textAlign: 'right' }}>
          <Text style={{ fontSize: 17 }}>{orgId}</Text>
        </View>
      </View>
    </Page>
  );
};

export const OrderStickers = ({ orders, orgId }: WaybillProps) => {
  return (
    <Document>
      {orders
        .sort((a, b) => (parseInt(a.order_id) < parseInt(b.order_id) ? -1 : 1))
        .map((order) =>
          [...Array(order.number_of_packages)].map((_, index) => (
            <OrderSticker
              key={`${order.id}-${index}`}
              order={order}
              orgId={orgId}
              package_index={index}
            />
          ))
        )}
    </Document>
  );
};
